<div class="user-profile-page h-full w-full overflow-y-auto bg-transparent p-4">
  @switch (loadingState()) {
    @case ('loading') {
      <atom-loading-inline title="Loading business profile" message="Fetching business details…" />
    }
    @case ('error') {
      <p class="m-0 text-sm text-destructive">Unable to load business profile.</p>
    }
    @case ('loaded') {
      @if (business(); as b) {
        <div class="flex flex-col gap-4 max-w-6xl w-full mx-auto">
          <section
            class="flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <div class="flex items-center gap-4 min-w-0">
              @if (b.companyLogo) {
                <img
                  [src]="imageService.getImageSource(b.companyLogo, 'gallery')"
                  alt="Logo for {{ b.companyName }}"
                  class="size-24 shrink-0 rounded-lg object-cover border border-border/60" />
              } @else {
                <div
                  class="size-16 shrink-0 rounded-lg border border-border/60 bg-muted/50 flex items-center justify-center text-secondary"
                  aria-hidden="true">
                  <svg class="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l8-4v18" />
                    <path d="M19 21V11l-6-4" />
                  </svg>
                </div>
              }
            </div>

            <div class="flex flex-col items-end gap-1 min-w-0 ml-auto">
              <h1 class="text-lg font-semibold text-primary m-0 text-right">{{ b.companyName }}</h1>
              <div class="flex min-w-0 items-center justify-end gap-2">
                <p
                  class="m-0 max-w-72 overflow-hidden text-ellipsis whitespace-nowrap font-mono text-xs text-secondary md:max-w-96"
                  [title]="b.id">
                  {{ b.id }}
                </p>
                <button
                  type="button"
                  class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-zinc-200/60 text-secondary transition-[opacity,color,border-color] duration-150 hover:text-primary hover:opacity-[0.85] dark:bg-zinc-600/60"
                  [class]="copyIdButtonClass()"
                  (click)="copyBusinessId(b.id)"
                  [attr.aria-label]="copyIdAriaLabel()">
                  @if (businessIdCopied()) {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      aria-hidden="true">
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  } @else {
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      aria-hidden="true">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
                    </svg>
                  }
                </button>
              </div>
            </div>
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Profile</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">About</p>
                <p class="m-0 mt-1 text-sm text-primary whitespace-pre-wrap">{{ b.about || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Status</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.isActive ? 'Active' : 'Inactive' }}</p>
              </div>
            </div>
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Business information</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">Website</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.website || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Instagram</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.instagram || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">TikTok</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.tiktok || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Currency</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.currencyCode || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Created</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ formatDate(b.createdDate) }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Pay as you go</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.isPayg ? 'Yes' : 'No' }}</p>
              </div>
            </div>
          </section>

          <section
            class="rounded-xl border-2 border-zinc-400/35 bg-zinc-200/80 p-4 sm:p-5 dark:border-zinc-500/45 dark:bg-indigo-800/20">
            <h2 class="m-0 mb-3 text-sm font-semibold tracking-wide text-primary">Billing</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p class="m-0 text-xs text-secondary">Stripe customer ID</p>
                <p class="m-0 mt-1 text-sm font-mono text-primary">{{ b.stripeCustomerId || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">VAT number</p>
                <p class="m-0 mt-1 text-sm text-primary">{{ b.vatNumber || '—' }}</p>
              </div>
              <div>
                <p class="m-0 text-xs text-secondary">Agency ID</p>
                <p class="m-0 mt-1 text-sm font-mono text-primary">{{ b.agencyId || '—' }}</p>
              </div>
            </div>
          </section>
        </div>
      }
    }
  }
</div>
